/**
 * Per-tool permissions, and Dev mode.
 *
 * GET /api/dev reports what the caller holds, one flag per tool (api/shared/devRole.ts):
 *
 *   comments — on the reviewer list (access.app_member, permission 'comments')
 *   viewAs   — full-access role AND a 'view_as' row: the View as button
 *   admin    — full-access role AND an 'admin' row: the Rules page (docs/RULES.md)
 *   statements — full-access role: the Statements admin page (rollout, held statements)
 *
 * View as and Rules show whenever the caller holds them; Dev mode has nothing to do with
 * them (Yehuda, 2026-10-06). Dev mode governs Comments only — and any future dev-only
 * tool. It is on by default, so reviewers see Comments without looking for a switch, and
 * the switch is shown only to people who hold comments. Off, Comments are hidden.
 *
 * All of this is presentation, not permission: the API checks every comment, rules and
 * view-as request itself, whatever this says.
 *
 * Only turning Dev mode OFF is remembered, per browser (localStorage '0').
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { TOKEN_HEADER } from '@/data/client'
import { apiFetch } from '@/auth/apiFetch'
import { exitViewAs, viewingAs } from '@/data/viewAs'

const KEY = 'cpc.devMode'

export interface DevFeatures {
  comments: boolean
  viewAs: boolean
  admin: boolean
  statements: boolean
}

interface DevModeState {
  /** What this person holds; null while asking. */
  features: DevFeatures | null
  /** Whether the Dev mode switch is shown: only for people holding comments. */
  available: boolean
  /** Dev mode: shows Comments. Says nothing about View as or Rules. */
  on: boolean
  setOn: (on: boolean) => void
}

const NONE: DevFeatures = { comments: false, viewAs: false, admin: false, statements: false }

const Ctx = createContext<DevModeState>({ features: NONE, available: false, on: false, setOn: () => {} })

export const useDevMode = () => useContext(Ctx)

/** The /api/dev body. Anything not exactly `true` is false. */
export function readFeatures(f: Partial<DevFeatures>): DevFeatures {
  return { comments: f.comments === true, viewAs: f.viewAs === true, admin: f.admin === true, statements: f.statements === true }
}

/** Which tools show, given what the caller holds and the Dev mode switch. Only Comments depend on it. */
export function shownTools(features: DevFeatures | null, devModeOn: boolean) {
  return {
    comments: !!features?.comments && devModeOn,
    viewAs: !!features?.viewAs,
    rules: !!features?.admin,
    statements: !!features?.statements,
  }
}

function readStored(): boolean {
  try {
    // On unless this browser turned it off.
    return window.localStorage.getItem(KEY) !== '0'
  } catch {
    return true
  }
}

export function DevModeProvider({ children }: { children: ReactNode }) {
  const { token, ready } = useAuth()
  const [features, setFeatures] = useState<DevFeatures | null>(null)
  const [stored, setStored] = useState(readStored)

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers[TOKEN_HEADER] = token
    apiFetch('/api/dev', { headers })
      .then((r) => (r.ok ? (r.json() as Promise<Partial<DevFeatures>>) : NONE))
      .then((f) => !cancelled && setFeatures(readFeatures(f)))
      // An older API (404) or anything else: no tools.
      .catch(() => !cancelled && setFeatures(NONE))
    return () => {
      cancelled = true
    }
  }, [token, ready])

  const available = !!features && features.comments
  // A switch that is not offered is off, whatever an earlier session stored.
  const on = available && stored

  // In a view once it is known this person may not view as anyone: back to their own
  // view. While still asking, leave it — the API decides.
  useEffect(() => {
    if (viewingAs() && features && !features.viewAs) exitViewAs()
  }, [features])

  const setOn = useCallback((next: boolean) => {
    try {
      if (next) window.localStorage.removeItem(KEY)
      else window.localStorage.setItem(KEY, '0')
    } catch {
      /* the switch still works for this page */
    }
    setStored(next)
  }, [])

  return <Ctx.Provider value={{ features, available, on, setOn }}>{children}</Ctx.Provider>
}
